<!-- @component
  Table page listing all quantity units (Mengeneinheiten).
  Displays a filtered grid of QuantityUnit rows with inline
  edit (click row) and delete (ActionMenu) actions.
-->
<script lang="ts">
	import type { QuantityUnit } from './types/quantity'
	import { DefaultQuantityUnit } from './types/quantity'
	import { t } from '$i18n/i18n'
	import { modals } from 'svelte-modals'
	import { mmpvState } from '#lib/mmpv/mmpv.state.svelte.js'
	import { applyFilters } from '$utils/filter'
	import { deleteQuantity } from './mmpv-quantities.controller.svelte'
	import ActionMenu from '#lib/organisms/ActionMenu.svelte'
	import ActionMenuItem from '#lib/molecules/ActionMenuItem.svelte'
	import Modal from '#lib/organisms/Modal.svelte'
	import iconTrashRed from '@/icons/icon_trash_red.svg'
	import iconEditGreen from '@/icons/icon_edit_green.svg'
	import QuantitiesModal from './QuantitiesModal.svelte'

	const filters = $derived(mmpvState.filters)

	function selectQuantity(unit: QuantityUnit) {
		modals.open(QuantitiesModal, {
			quantityUnit: { ...DefaultQuantityUnit(), ...unit },
			isNew: false
		})
	}

	function handleDelete(unit: QuantityUnit) {
		modals.open(Modal, {
			title: $t('UI.mmpv.delete.modal.title', { entity: unit.label }),
			message: $t('UI.mmpv.quantities.delete.modal.message', { name: unit.label }),
			denyText: $t('UI.delete'),
			onDeny: () => {
				deleteQuantity(unit)
				modals.close()
			}
		})
	}
</script>

<div class="quantities-container">
	<div class="quantities-table">
		<div class="row-wrapper">
			<div>{$t('UI.mmpv.quantities.name')}</div>
			<div>{$t('UI.mmpv.quantities.label')}</div>
			<div>{$t('UI.mmpv.quantities.shortLabel')}</div>
			<div>{$t('UI.mmpv.quantities.unit')}</div>
			<div>{$t('UI.mmpv.quantities.materialCount')}</div>
			<div>{$t('UI.mmpv.catalogs.action')}</div>
		</div>
		{#each mmpvState.quantities.filter( (unit) => applyFilters(filters, unit) ) as unit (unit.publicId)}
			<div
				class="row-wrapper"
				role="button"
				tabindex="0"
				onclick={() => selectQuantity(unit)}
				onkeydown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						selectQuantity(unit)
					}
				}}
			>
				<div title={unit.name || ''}>{unit.name || ''}</div>
				<div title={unit.label || ''}>{unit.label || ''}</div>
				<div title={unit.shortLabel || ''}>{unit.shortLabel || ''}</div>
				<div title={unit.unit || ''}>{unit.unit || ''}</div>
				<div>{unit.materialNodeIds?.length ?? 0}</div>
				<div>
					<ActionMenu
						titleAttr={$t('UI.mmpv.actionMenu.titleAttribute')}
						appendToElement={document.body}
					>
						{#snippet content()}
							<ActionMenuItem
								label={$t('UI.edit')}
								iconSrc={iconEditGreen}
								onclick={() => selectQuantity(unit)}
							/>
							<ActionMenuItem
								label={$t('UI.delete')}
								iconSrc={iconTrashRed}
								onclick={() => handleDelete(unit)}
							/>
						{/snippet}
					</ActionMenu>
				</div>
			</div>
		{/each}
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.quantities-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		width: 100%;
	}

	.quantities-table {
		display: grid;
		grid-template-columns: 1fr 1fr 0.8fr 0.7fr 0.7fr minmax(60px, 0.15fr);
		grid-template-rows: 1fr;
		font-family: Roboto, sans-serif;
		line-height: 18px;
		font-weight: 400;
		font-size: 0.75rem;
		color: black;
		width: 100%;

		& .row-wrapper {
			display: contents;

			& > div {
				text-align: start;
				border-bottom: 1px solid var(--color-gray-100);
				padding: 0.75rem 0.75rem;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;

				&:last-child {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
					overflow: visible;
				}

				&:hover {
					cursor: pointer;
				}
			}

			&:first-child > div {
				border-bottom: 1px solid var(--primary-green);
				font-family: Roboto, sans-serif;
				line-height: 18px;
				font-weight: 400;
				font-size: 0.875rem;
				color: black;
			}
		}
	}
</style>
